import { useEffect, useRef } from 'react'

import { useAnnounce } from '@/shared/providers/announcer-provider'

type Page<T> = { items: T[]; pageInfo: { hasNextPage: boolean } }

type PagedQuery<T> = {
  data?: { pages: Page<T>[] }
  hasNextPage: boolean
  dataUpdatedAt?: number
  isFetching?: boolean
  isFetchingNextPage: boolean
  isFetchNextPageError?: boolean
  fetchNextPage: (
    options?: { cancelRefetch?: boolean },
  ) => Promise<{ data?: { pages: Page<T>[] }; isError?: boolean }>
}

function countItems<T>(pages: Page<T>[]): number {
  return pages.reduce((total, page) => total + page.items.length, 0)
}

function tail(hasNextPage: boolean): string {
  return hasNextPage ? '.' : ', end of results.'
}

export function useLoadMore<T>(
  query: PagedQuery<T>,
  noun: string,
  label = 'Load more',
  options: { criteriaKey?: string; singularNoun?: string } = {},
) {
  const announce = useAnnounce()
  const statusRef = useRef<HTMLParagraphElement>(null)
  const criteriaRef = useRef(options.criteriaKey)
  useEffect(() => {
    criteriaRef.current = options.criteriaKey
  })
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const items = query.data?.pages.flatMap((page) => page.items) ?? []

  function load(fetchOptions: { cancelRefetch?: boolean } | undefined, focusOnEnd: boolean) {
    const loadedBefore = items.length
    const criteriaAtStart = criteriaRef.current
    void query.fetchNextPage(fetchOptions).then((result) => {
      if (!mountedRef.current || result.isError || criteriaRef.current !== criteriaAtStart) return
      const pages = result.data?.pages ?? []
      const hasNextPage = pages[pages.length - 1]?.pageInfo.hasNextPage ?? false
      announce(
        `Loaded ${countItems(pages) - loadedBefore} more ${noun}${tail(hasNextPage)}`,
      )
      if (focusOnEnd && !hasNextPage) statusRef.current?.focus({ preventScroll: true })
    })
  }

  return {
    items,
    hasNextPage: query.hasNextPage,
    dataUpdatedAt: query.dataUpdatedAt ?? 0,
    isFetching: query.isFetching ?? query.isFetchingNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError ?? false,
    label: query.isFetchingNextPage ? 'Loading…' : label,
    retryMessage: `Couldn’t load more ${noun}.`,
    autoLoad: () => load({ cancelRefetch: false }, false),
    buttonProps: {
      type: 'button' as const,
      disabled: query.isFetchingNextPage,
      onClick: () => load(undefined, true),
    },
    statusProps: {
      ref: statusRef,
      tabIndex: -1,
      className: 'sr-only',
      children: `Showing ${items.length} ${items.length === 1 ? options.singularNoun ?? noun : noun}${tail(query.hasNextPage)}`,
    },
  }
}
